import { useState, useEffect } from "react";

import { toast } from "react-toastify";

import { THIS_OR_THAT } from "../../../../constants";
import { JourneyContentRow } from "../../../../types/games";

import { Check, X } from "lucide-react";

/**
 * Props for the This or That form component
 */
interface JourneyThisOrThatFormProps {
  contentRows: JourneyContentRow[];
  setContentRows: React.Dispatch<React.SetStateAction<JourneyContentRow[]>>;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isLoading: boolean;
  touchpointId: string; // ID of the touchpoint this form is associated with
}

/**
 * A component for creating and editing This or That questions in journeys.
 *
 * This component doesn't maintain its own state for rows - it only renders rows from
 * the parent component's state and calls parent methods to update that state.
 */
export const JourneyThisOrThatCreateForm = ({
  contentRows,
  setContentRows,
  isLoading,
  touchpointId,
}: JourneyThisOrThatFormProps) => {
  // Local state to track input values without losing focus on keystrokes
  const [localInputValues, setLocalInputValues] = useState<
    Record<number, Record<string, string>>
  >({});

  // Get only the valid rows for this touchpoint and game type
  const relevantRows = contentRows.filter(
    (row) => row.touchpoint_id === touchpointId,
  );

  // Filter rows for preview - only show rows with both choices filled
  const previewRows = relevantRows.filter((row) =>
    Boolean(row.choice_1 && row.choice_2),
  );

  // Add a new row with proper touchpoint and game identifiers
  const addEmptyRow = () => {
    const newRow = {
      id: Date.now(),
      touchpoint_id: touchpointId,
      game_type: THIS_OR_THAT,
      choice_1: "",
      choice_2: "",
    };

    // Update parent state directly
    setContentRows((current) => [...current, newRow]);
  };

  // Handle input change locally to prevent losing focus
  const handleInputChange = (id: number, field: string, value: string) => {
    setLocalInputValues((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  // Get value from local state first, fallback to parent state
  const getInputValue = (row: JourneyContentRow, field: string): string => {
    if (
      localInputValues[row.id] &&
      localInputValues[row.id][field] !== undefined
    ) {
      return localInputValues[row.id][field];
    }
    return row[field] || "";
  };

  // Check if a row has all required fields filled in the local state
  const isRowValid = (id: number): boolean => {
    const row = contentRows.find((r) => r.id === id);
    if (!row) return false;

    // Get values from local state or parent state
    const choice1 = getInputValue(row, "choice_1");
    const choice2 = getInputValue(row, "choice_2");

    // Both choices must be filled
    return Boolean(choice1 && choice2);
  };

  // Remove a row from parent state
  const removeRow = (idToRemove: number) => {
    setContentRows((prevRows) => {
      // Filter out the row to remove
      const filteredRows = prevRows.filter((row) => row.id !== idToRemove);

      // Get the remaining rows for this touchpoint/game
      const remainingForTouchpoint = filteredRows.filter(
        (row) => row.touchpoint_id === touchpointId,
      );

      // If we removed the last row for this touchpoint, add a new empty one
      if (remainingForTouchpoint.length === 0) {
        return [
          ...filteredRows,
          {
            id: Date.now(),
            touchpoint_id: touchpointId,
            game_type: THIS_OR_THAT,
          },
        ];
      }

      return filteredRows;
    });
  };

  // Save all valid rows from local state to parent state, replacing existing ones for this TP/Game
  const saveRowsToPreview = (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Identify valid rows based on current local input state and construct their data
    const locallyValidRowsData = relevantRows
      .map((row) => {
        if (isRowValid(row.id)) {
          // Construct the full row data from local state
          return {
            ...row, // Keep existing fields like id, touchpoint_id, game_type
            type: "this_or_that", // Ensure API compatibility type
            choice_1: getInputValue(row, "choice_1"),
            choice_2: getInputValue(row, "choice_2"),
          } as JourneyContentRow; // Cast to ContentRow type
        }
        return null; // Return null for invalid rows
      })
      .filter((row): row is JourneyContentRow => row !== null); // Filter out the nulls and assert type

    if (locallyValidRowsData.length === 0) {
      toast.error(
        "Please fill in both choices for at least one question before saving.",
      );
      return;
    }

    // 2. Update parent state: save valid rows and add a new empty one
    setContentRows((prevRows) => {
      // Filter out all existing rows for this specific touchpoint and game
      const otherRows = prevRows.filter(
        (row) => !(row.touchpoint_id === touchpointId),
      );

      // Add the newly validated rows and a new empty row
      return [
        ...otherRows,
        ...locallyValidRowsData,
        {
          id: Date.now(),
          touchpoint_id: touchpointId,
          game_type: THIS_OR_THAT,
          choice_1: "",
          choice_2: "",
        },
      ];
    });

    // 3. Clear local state for the saved rows
    const savedRowIds = new Set(locallyValidRowsData.map((row) => row.id));
    setLocalInputValues((prev) => {
      const newState = { ...prev };
      savedRowIds.forEach((id) => {
        delete newState[id];
      });
      return newState;
    });

    toast.success(
      `${locallyValidRowsData.length} question${
        locallyValidRowsData.length !== 1 ? "s" : ""
      } saved successfully!`,
    );
  };

  // Ensure there's always at least one row to edit
  useEffect(() => {
    if (relevantRows.length === 0) {
      addEmptyRow();
    }
  }, []); // Only run on mount

  return (
    <div className="space-y-4 relative">
      {/* Preview of saved items - only show when there are valid saved rows */}
      {previewRows.length > 0 && (
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Saved Questions
            </h4>
            <span className="text-xs text-gray-500">
              {previewRows.length} total
            </span>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {previewRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between bg-white p-2 rounded-md text-sm border border-gray-100"
              >
                <div className="flex-1 truncate">
                  <span className="font-medium">{row.choice_1}</span>
                  <span className="mx-2 text-brand font-smll">or</span>
                  <span className="font-medium">{row.choice_2}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form with multiple editable rows */}
      <form onSubmit={saveRowsToPreview} className="pt-4">
        {/* Add Row button */}
        <div className="flex justify-end items-center mb-4 gap-x-4">
          <button
            onClick={saveRowsToPreview}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
          >
            <Check className="w-4 h-4 inline-block pr-2" />
            {isLoading ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            onClick={addEmptyRow}
            className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
          >
            + Add Row
          </button>
        </div>

        {/* Multiple editable rows */}
        <div className="space-y-3 mb-4">
          {relevantRows
            // Only show empty/incomplete rows in the editing form
            .filter(
              (row: JourneyContentRow) =>
                !row.choice_1 || !row.choice_2 || relevantRows.length === 0,
            )
            .map((row: JourneyContentRow) => (
              <div
                key={row.id}
                className="flex flex-col p-3 rounded-md bg-white shadow-sm border border-gray-200"
              >
                {/* Choices section */}
                <div className="flex-grow flex flex-col space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Choice 1
                      </label>
                      <input
                        type="text"
                        placeholder="😺 cats"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                        value={getInputValue(row, "choice_1")}
                        onChange={(e) =>
                          handleInputChange(row.id, "choice_1", e.target.value)
                        }
                      />
                    </div>

                    <div className="mt-4">
                      <span className="text-sm font-medium text-brand">or</span>
                    </div>

                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Choice 2
                      </label>
                      <input
                        type="text"
                        placeholder="🐶 dogs"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                        value={getInputValue(row, "choice_2")}
                        onChange={(e) =>
                          handleInputChange(row.id, "choice_2", e.target.value)
                        }
                      />
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        disabled={isLoading || relevantRows.length === 1}
                        className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </form>
      <div>
        <span className="text-xs font-medium text-gray-700">
          {previewRows.length} questions ready
        </span>
      </div>
    </div>
  );
};
